import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CoursesPage.css';
import './NotesPage.css';
import DotGrid from './DotGrid';
import { supabase } from './supabaseClient';
import FloatingCart from './components/FloatingCart';
import Navbar from './components/Navbar';

export default function NotesPage() {
  const [activeFilter, setActiveFilter] = useState('All Notes');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [showAll, setShowAll] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchNotes();
  }, []);

  useEffect(() => {
    setShowAll(false);
  }, [searchQuery, activeCategory, activeFilter]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      // pdf_url is intentionally excluded — see NoteDetailsPage.jsx. select('*')
      // would fail outright (not just omit pdf_url) once the DB grants are
      // column-scoped, since a bare '*' expands before privilege checks.
      const { data, error } = await supabase
        .from('notes')
        .select('id, title, description, category, type, university, pages, price, original_price, thumbnail_url, preview_pdf_url, includes, tags, is_published, created_at');
      if (error) throw error;
      setNotes(data || []);
    } catch (err) {
      console.error('Error fetching notes:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };



  const filteredNotes = notes.filter(note => {
    const matchesSearch =
      note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (note.description && note.description.toLowerCase().includes(searchQuery.toLowerCase()));
    let matchesType = true;
    if (activeFilter === 'Handwritten') matchesType = note.type === 'handwritten';
    else if (activeFilter === 'Printed') matchesType = note.type === 'printed';
    let matchesCategory = true;
    if (activeCategory !== 'All') {
      matchesCategory = (note.category || '').toLowerCase() === activeCategory.toLowerCase();
    }
    return matchesSearch && matchesType && matchesCategory;
  }).sort((a, b) => {
    if (a.is_published && !b.is_published) return -1;
    if (!a.is_published && b.is_published) return 1;
    return 0;
  });

  const displayedNotes = showAll ? filteredNotes : filteredNotes.slice(0, 6);

  return (
    <div className="courses-page-container document-explorer-page">
      <div className="cp-bg-wrapper">
        <DotGrid dotSize={2} gap={15} baseColor="#004f54" activeColor="#00e5c8"
          proximity={120} shockRadius={250} shockStrength={5} resistance={750} returnDuration={1.5} />
      </div>

      <Navbar />

      <main className="cp-main">
        {/* Header */}
        <section className="cp-header-section">
          <div>
            <h1 className="cp-title"><span className="cp-gradient-text">Notes</span> Section</h1>
            <p className="cp-subtitle">
              Premium PDF notes — handwritten &amp; printed — for every engineering discipline. Download and ace your exams.
            </p>
          </div>
          <div className="cp-search-wrapper">
            <svg className="cp-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input type="text" className="cp-search-input" placeholder="Search notes by subject or topic..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </section>

        <div className="cp-layout">
          {/* Sidebar */}
          <aside className="cp-sidebar">
            <div className="glass-panel cp-explorer">
              <h3 onClick={() => setActiveCategory('All')} style={{ cursor: 'pointer', color: activeCategory === 'All' ? 'var(--cyan)' : 'var(--ink)', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>folder_open</span>
                  EXPLORER
                </span>
                {activeCategory !== 'All' && (
                  <span className="material-symbols-outlined" style={{ fontSize: '1.1rem', color: 'var(--rose)' }} title="Clear Filter">filter_alt_off</span>
                )}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { key: 'CSE & IT', cat: 'Programming', icon: 'terminal', label: 'Programming' },
                  { key: 'Mechanics', cat: 'Mechanics', icon: 'settings_suggest', label: 'Mechanics' },
                  { key: 'Drawing', cat: 'Graphics & Drawing', icon: 'architecture', label: 'Graphics & Drawing' },
                  { key: 'Electrical', cat: 'Electrical & Electronics', icon: 'electric_bolt', label: 'Electrical & Electronics' },
                  { key: 'Mathematics', cat: 'Mathematics', icon: 'calculate', label: 'Mathematics' },
                  { key: 'English', cat: 'English', icon: 'translate', label: 'English' },
                  { key: 'Chemistry', cat: 'Chemistry', icon: 'science', label: 'Chemistry' },
                ].map(({ key, cat, icon, label }) => (
                  <div key={key}>
                    <button className="cp-branch-btn" onClick={() => setActiveCategory(cat)}>
                      <div className="cp-branch-icon">
                        <span className="material-symbols-outlined" style={{ color: activeCategory === cat ? 'var(--cyan)' : '#c0c1ff', fontSize: '1.2rem' }}>{icon}</span>
                        <span style={{ color: activeCategory === cat ? 'var(--cyan)' : 'inherit', fontWeight: activeCategory === cat ? 'bold' : 'normal' }}>{label}</span>
                      </div>
                      <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'var(--muted)' }}>chevron_right</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="cp-promo glass-panel">
              <img src="/assets/Firefly.png" alt="Promo" />
              <div className="cp-promo-content">
                <span className="cp-badge-upcoming">UPCOMING</span>
                <h4 style={{ fontFamily: 'var(--font-display)', margin: 0, fontSize: '1.1rem' }}>Full Semester Pack</h4>
              </div>
            </div>
          </aside>

          {/* Main Grid */}
          <div>
            <div className="cp-filters-bar glass-panel">
              <div className="cp-filter-chips">
                {['All Notes', 'Handwritten', 'Printed'].map(f => (
                  <button key={f} className={`cp-chip ${activeFilter === f ? 'active' : ''}`} onClick={() => setActiveFilter(f)}>{f}</button>
                ))}
              </div>
              <div className="cp-sort">
                <span style={{ color: 'var(--muted)', fontSize: '0.85rem', marginRight: '0.5rem' }}>Sort by:</span>
                <select><option>Latest</option><option>Popularity</option></select>
              </div>
            </div>

            <div className="cp-grid">
              {loading ? (
                <>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className={`glass-panel cp-card cp-skeleton-card ${(i + 1) % 3 === 0 ? 'wide' : ''}`}>
                      <div className="cp-card-img-wrapper skeleton-box"></div>
                      <div className="cp-card-body">
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.8rem' }}>
                          <div className="skeleton-box skeleton-badge"></div>
                          <div className="skeleton-box skeleton-badge"></div>
                        </div>
                        <div className="skeleton-box skeleton-title"></div>
                        <div className="skeleton-box skeleton-text"></div>
                        <div className="skeleton-box skeleton-text short"></div>
                        <div className="cp-card-footer" style={{ marginTop: 'auto' }}>
                          <div className="skeleton-box skeleton-instructor"></div>
                          <div className="skeleton-box skeleton-btn"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : error ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--rose)', gridColumn: '1 / -1' }}>
                  <p>Failed to load notes: {error}</p>
                </div>
              ) : filteredNotes.length === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)', gridColumn: '1 / -1' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>description</span>
                  <p>No notes match your filter criteria.</p>
                </div>
              ) : (
                displayedNotes.map((note, index) => {
                  const isWide = (index + 1) % 3 === 0;
                  return (
                    <Link 
                      to={note.is_published ? `/note/${note.id}` : '#'} 
                      onClick={(e) => { if (!note.is_published) e.preventDefault(); }} 
                      key={note.id} 
                      className={`glass-panel cp-card ${isWide ? 'wide' : ''} ${!note.is_published ? 'cp-card-unpublished' : ''}`} 
                      style={{ textDecoration: 'none' }}>
                      <div className="cp-card-img-wrapper">
                        {note.thumbnail_url ? (
                          <img src={note.thumbnail_url} alt={note.title} style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'var(--bg2)' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', background: 'var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: 'var(--cyan)', opacity: 0.5 }}>picture_as_pdf</span>
                          </div>
                        )}
                        <div className="cp-play-btn">
                          <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>picture_as_pdf</span>
                        </div>
                        {note.pages && <div className="cp-rating">{note.pages}pp</div>}
                      </div>
                      <div className="cp-card-content">
                        <div className="cp-card-tags">
                          <span className="cp-tag">{note.category || 'NOTES'}</span>
                          {note.type && <span className="cp-tag" style={{ background: 'rgba(0,200,120,0.15)', color: '#00c878', border: '1px solid rgba(0,200,120,0.2)' }}>{note.type}</span>}
                        </div>
                        <h3 className="cp-card-title">{note.title}</h3>
                        <p className="cp-card-desc">{note.description}</p>
                        <div className="cp-card-footer">
                          <div className="cp-price-wrap">
                            <span className="cp-price-label">PDF Access</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                              <span className="cp-price">
                                {typeof note.price === 'number' ? `₹${note.price}` : (note.price || 'Free')}
                              </span>
                              {typeof note.price === 'number' && (
                                <span style={{ fontSize: '0.72rem', color: '#ff4e6a', fontWeight: '800', background: 'rgba(255,78,106,0.12)', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>SALE</span>
                              )}
                            </div>
                          </div>
                          {note.is_published ? (
                            <button className="cp-btn">Get PDF</button>
                          ) : (
                            <button className="cp-btn cp-btn-unpublished" disabled>Unavailable</button>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>

            {filteredNotes.length > 6 && (
              <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                <button onClick={() => setShowAll(!showAll)} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: '0.85rem', fontWeight: 'bold', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  {showAll ? 'Show Less' : 'Load More Notes'}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: showAll ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="em-footer">
        <div className="fg">
          <div className="fb">
            <a href="/" className="em-logo"><span className="l1">ENGI</span><span className="l2">&lt;mitra&gt;</span></a>
            <p>Built for 1st year engineering students. Expert notes, courses, and MPQs for every major Indian university — since 2022.</p>
          </div>
          <div className="fc">
            <h5>Platform</h5>
            <ul>{["Courses", "Notes", "MPQs"].map(l => <li key={l}><a href={l === 'Courses' ? '/courses' : `/${l.toLowerCase()}`}>{l}</a></li>)}</ul>
          </div>
          <div className="fc">
            <h5>Community</h5>
            <ul>
              <li><a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer">Instagram</a></li>
              <li><a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer">Facebook</a></li>
              <li><a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer">WhatsApp Group</a></li>
            </ul>
          </div>
          <div className="fc">
            <h5>Legal</h5>
            <ul>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
              <li><Link to="/refund-policy">Refund Policy</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
            </ul>
          </div>
        </div>
        <div className="fb-bottom">
          <p>© 2026 ENGImitra. All rights reserved.</p>
          <div className="soc-row">
            <a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer" className="soc" aria-label="Instagram">ig</a>
            <a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer" className="soc" aria-label="Facebook">fb</a>
            <a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer" className="soc" aria-label="WhatsApp Group">wa</a>
          </div>
        </div>
      </footer>
      <FloatingCart />
    </div>
  );
}
